import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Users, Hash, Lock, ExternalLink, Settings, AlertCircle, CheckCircle, Plus } from 'lucide-react';
import * as sdk from 'matrix-js-sdk';
import ClaraChatRoom from './ClaraChatRoom';

const ClaraChatModal = ({ isOpen, onClose }) => {
  const [matrixClient, setMatrixClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]); // Start with empty array - load real rooms from Matrix
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomTopic, setNewRoomTopic] = useState('');

  // Initialize Matrix client
  useEffect(() => {
    if (isOpen && !matrixClient) {
      initializeMatrixClient();
    }
  }, [isOpen]);

  const initializeMatrixClient = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      // Check for existing credentials
      const accessToken = localStorage.getItem('clara_matrix_token');
      const userId = localStorage.getItem('clara_matrix_user_id');
      const deviceId = localStorage.getItem('clara_matrix_device_id');

      if (accessToken && userId) {
        // Use existing credentials
        const client = sdk.createClient({
          baseUrl: "https://clara360.de",
          accessToken: accessToken,
          userId: userId,
          deviceId: deviceId,
        });

        await client.startClient();
        setMatrixClient(client);
        setIsConnected(true);
        loadRoomsFromMatrix(client);
      } else {
        // Try to connect without credentials to test server
        console.log('No Matrix credentials found, testing server connection...');
        try {
          const testClient = sdk.createClient({
            baseUrl: "https://clara360.de"
          });
          
          // Test server connection
          const serverVersions = await testClient.getVersions();
          console.log('Matrix server versions:', serverVersions);
          setError('Matrix Server erreichbar! Bitte registrieren Sie sich oder loggen Sie sich ein.');
          setIsConnected(false);
        } catch (testErr) {
          console.error('Matrix server test failed:', testErr);
          setError('Matrix Server nicht erreichbar. Demo-Modus aktiv.');
          setIsConnected(false);
        }
      }
    } catch (err) {
      console.error('Matrix client initialization error:', err);
      setError('Verbindung zum Matrix Server fehlgeschlagen. Demo-Modus aktiv.');
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const loadRoomsFromMatrix = (client) => {
    try {
      const matrixRooms = client.getRooms();
      const allRooms = matrixRooms
        .filter(room => room.name) // Load all rooms that have a name
        .map(room => ({
          id: room.roomId,
          name: room.name,
          members: room.getJoinedMemberCount(),
          lastMessage: getLastMessage(room),
          encrypted: room.hasEncryptionStateEvent(),
          unread: room.getUnreadNotificationCount()
        }));

      setRooms(allRooms);
      console.log(`Loaded ${allRooms.length} rooms from Matrix`);
    } catch (err) {
      console.error('Error loading rooms from Matrix:', err);
    }
  };

  const createRoom = async () => {
    if (!matrixClient || !newRoomName.trim()) return;
    
    try {
      const roomOptions = {
        name: newRoomName.trim(),
        topic: newRoomTopic.trim(),
        preset: 'public_chat',
        visibility: 'public'
      };
      
      const response = await matrixClient.createRoom(roomOptions);
      console.log('Room created:', response.room_id);
      
      // Refresh rooms list
      loadRoomsFromMatrix(matrixClient);
      
      // Reset form
      setNewRoomName('');
      setNewRoomTopic('');
      setShowCreateRoom(false);
      
    } catch (err) {
      console.error('Error creating room:', err);
      setError('Fehler beim Erstellen des Raums: ' + err.message);
    }
  };

  const getLastMessage = (room) => {
    try {
      const timeline = room.getLiveTimeline();
      const events = timeline.getEvents();
      const messageEvents = events.filter(event => event.getType() === 'm.room.message');
      
      if (messageEvents.length > 0) {
        const lastEvent = messageEvents[messageEvents.length - 1];
        return lastEvent.getContent().body;
      }
    } catch (err) {
      console.error('Error getting last message:', err);
    }
    return 'Keine Nachrichten';
  };

  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
  };

  const handleElementWebClick = () => {
    window.open('https://app.element.io/#/welcome', '_blank');
  };

  const handleMatrixSetup = () => {
    // Open Matrix setup/login modal (to be implemented)
    alert('Matrix Setup wird implementiert...');
  };

  if (!isOpen) return null;

  if (selectedRoom) {
    return (
      <ClaraChatRoom 
        room={selectedRoom} 
        onClose={() => setSelectedRoom(null)}
        matrixClient={matrixClient}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl h-[80vh] bg-white dark:bg-gray-900 rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-6 h-6" />
              <h2 className="text-xl font-bold">Clara Chat</h2>
            </div>
            <div className="hidden md:flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Hash className="w-4 h-4" />
                <span>{rooms.length} Räume</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{rooms.reduce((sum, room) => sum + room.members, 0)} Mitglieder</span>
              </div>
              <div className="flex items-center gap-1 bg-green-500 bg-opacity-20 px-2 py-1 rounded">
                <Lock className="w-3 h-3" />
                <span className="text-xs">Verschlüsselt</span>
              </div>
              <div className="flex items-center gap-1">
                {isConnected ? (
                  <>
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    <span className="text-xs">Matrix Verbunden</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3 h-3 text-yellow-400" />
                    <span className="text-xs">Demo Modus</span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleMatrixSetup}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Matrix Einstellungen"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Connection Status */}
        {isConnecting && (
          <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-3 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm">Verbindung zu Matrix Server...</span>
          </div>
        )}

        {error && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Room List */}
        <div className="flex-1 p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold dark:text-white mb-2">Chat-Räume</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Wählen Sie einen Raum für sichere Kommunikation
              </p>
            </div>
            {isConnected && (
              <button
                onClick={() => setShowCreateRoom(!showCreateRoom)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Raum erstellen
              </button>
            )}
          </div>

          {/* Create Room Form */}
          {showCreateRoom && (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border">
              <h4 className="font-medium dark:text-white mb-3">Neuen Raum erstellen</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Raumname *
                  </label>
                  <input
                    type="text"
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    placeholder="z.B. Hausmeister Chat"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beschreibung (optional)
                  </label>
                  <input
                    type="text"
                    value={newRoomTopic}
                    onChange={(e) => setNewRoomTopic(e.target.value)}
                    placeholder="z.B. Für Wartungsanfragen und Reparaturen"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={createRoom}
                    disabled={!newRoomName.trim()}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Erstellen
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateRoom(false);
                      setNewRoomName('');
                      setNewRoomTopic('');
                    }}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {rooms.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
                  {isConnected ? 'Keine Räume gefunden' : 'Nicht verbunden'}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
                  {isConnected 
                    ? 'Erstellen Sie Ihren ersten Chat-Raum um zu beginnen.'
                    : 'Verbinden Sie sich mit dem Matrix Server um Räume zu sehen.'
                  }
                </p>
                {!isConnected && (
                  <button
                    onClick={() => window.open('https://app.element.io/#/welcome', '_blank')}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Bei Element registrieren
                  </button>
                )}
              </div>
            ) : (
              rooms.map((room) => (
                <div
                  key={room.id}
                  onClick={() => handleRoomSelect(room)}
                  className="p-4 border dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center text-white font-semibold">
                        {room.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-medium dark:text-white">{room.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{room.lastMessage}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                        <Users className="w-4 h-4" />
                        <span>{room.members}</span>
                      </div>
                      {room.encrypted && (
                        <Lock className="w-4 h-4 text-green-500" />
                      )}
                      {room.unread > 0 && (
                        <div className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {room.unread}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Lock className="w-3 h-3" />
              <span>Ende-zu-Ende verschlüsselt • Matrix Protocol</span>
            </div>
            <button
              onClick={handleElementWebClick}
              className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Element Web öffnen</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaraChatModal;

